import React, { useMemo } from 'react';
import { Link } from '../../../UI/Elements/Link';
import { Label } from 'components/UI/Elements/Label/label';
import { FilterButton } from 'components/Ecommerce/CategoriesShopWithNav/Nav/CategoriesShopNav';
import { Button } from 'components/UI/Elements';
import { formatContentfulComponentProps } from 'utils/globals';
import { Image } from 'components/UI/Media';
import clsx from 'clsx';
import JsonLdBreadcrumb from 'components/Utils/JsonLD/JsonLdBreadcrumb';
import { useMediaQuery } from 'utils/hooks/useMediaQuery';
import { withLocale } from '../../../../utils/hocs';

const TextDescription = ({ cat, active, mainCategory }) => (
  <div>
    <div className={'flex justify-start text-left'}>
      <Label
        {...cat.label}
        weight={active ? 'bold' : 'medium'}
        size={'sm'}
        decoration={
          active && !mainCategory ? 'underline md:no-underline' : undefined
        }
        color={{ classname: active ? 'primary' : 'gray-600' }}
      />
    </div>
    <Label
      {...cat.subLabel}
      weight={'normal'}
      size={'sm'}
      color={{ classname: 'gray-800' }}
    />
  </div>
);
const SubCategory = ({ subCategory, active }) => {
  const props = formatContentfulComponentProps(subCategory);

  return (
    <Link slug={props.link?.slug}>
      <div
        className={clsx(
          'flex flex-row items-center md:w-auto transition-opacity duration-300 hover:opacity-100',
          {
            'md:opacity-80': !active,
          }
        )}
      >
        <div
          className={clsx(
            'bordered-breadcrumb',
            'overflow-hidden rounded-full w-11 h-11 mr-4',
            {
              'border-2 md:border': active,
            }
          )}
        >
          <Image height='100%' width='100%' {...props.image} />
        </div>
        <TextDescription
          cat={{ ...props, label: props.link?.label }}
          active={active}
        />
      </div>
    </Link>
  );
};
const Separator = () => (
  <div className={'h-10 flex justify-center transform rotate-12'}>
    <div className={'h-full border-l border-gray-600'} />
  </div>
);

const CollectionBreadcrumb = withLocale(
  ({ category, subCategories, onClick, locale }) => {
    const activeSlug = locale[3];
    const isDesktop = !useMediaQuery('md');

    const sortedSub = useMemo(() => {
      let subCategoriesItems = [...subCategories.items];

      if (!isDesktop && activeSlug) {
        // Set active category in first
        const activeIdx = subCategoriesItems.findIndex((c) => {
          const { link } = formatContentfulComponentProps(c);

          return link?.slug.url === activeSlug.url;
        });
        const activeCat = {
          ...subCategoriesItems.splice(activeIdx, 1)[0],
          active: activeIdx >= 0,
        };
        subCategoriesItems = [activeCat, ...subCategoriesItems].filter(
          (c) => !!c
        );
      } else if (activeSlug) {
        const idx = subCategoriesItems.findIndex((c) => {
          const { link } = formatContentfulComponentProps(c);
          return link?.slug.url === activeSlug.url;
        });

        if (idx >= 0) subCategoriesItems[idx].active = true;
      }

      return subCategoriesItems;
    }, [isDesktop, activeSlug, subCategories]);

    const jsonLinks = useMemo(() => {
      const links = [{ slug: { url: '' }, label: { text: 'Home' } }, category];
      if (activeSlug) {
        let subCategoriesItems = [...subCategories.items];
        const activeCategory = subCategoriesItems.find((c) => {
          const { link } = formatContentfulComponentProps(c);
          return link?.slug.url === activeSlug.url;
        });

        const { link } = formatContentfulComponentProps(activeCategory);

        if (link) {
          links.push({ ...link, label: link.label });
        }
      }

      return links;
    }, [category, activeSlug]);

    return (
      <>
        <JsonLdBreadcrumb links={jsonLinks} />
        <div className={'flex flex-row items-center pr-4'} onClick={onClick}>
          <div className={'hidden md:block '}>
            <Link
              slug={{ url: '' }}
              icon={{
                iconKey: 'FaHome',
                size: 6,
                color: { classname: 'gray-700' },
              }}
            />
          </div>
          <div className={'hidden md:block mx-4'}>
            <Separator />
          </div>
          <Link slug={category.slug}>
            <TextDescription cat={category} active mainCategory />
          </Link>
          <div className={'mx-4'}>
            <Separator />
          </div>
          <div className={'flex md:items-center w-max space-x-4 lg:mr-0 mr-6'}>
            {sortedSub.map((sub) => (
              <SubCategory key={sub.id} subCategory={sub} active={sub.active} />
            ))}
          </div>
        </div>
      </>
    );
  }
);

export default CollectionBreadcrumb;
